import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {boolean} - When true, ignore focus on inline search (prevents auto-focus on PLP/PDP load) */
  #pageLoadGuardActive = false;

  /** @type {number} - Timeout id for clearing page load guard */
  #pageLoadGuardTimeout = 0;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (window.__algoliaAutocompleteActive) {
      this.setAttribute('data-native-search-disabled', 'true');
    }

    if (this.refs.searchInput?.value?.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      // For inline search (no dialog), load empty state on focus (skip if Algolia has replaced the input)
      this.refs.searchInput?.addEventListener('focus', this.#handleInputFocus, { signal });

      // On PLP/PDP, prevent search from auto-focusing or opening on page load
      if (this.hasAttribute('data-prevent-search-autofocus')) {
        this.#pageLoadGuardActive = true;
        if (this.refs.searchInput && document.activeElement === this.refs.searchInput) {
          this.refs.searchInput.blur();
        }
        this.#pageLoadGuardTimeout = window.setTimeout(() => {
          this.#pageLoadGuardActive = false;
          this.#pageLoadGuardTimeout = 0;
        }, 3000);
        const clearGuard = () => {
          if (!this.#pageLoadGuardActive) return;
          this.#pageLoadGuardActive = false;
          if (this.#pageLoadGuardTimeout) {
            window.clearTimeout(this.#pageLoadGuardTimeout);
            this.#pageLoadGuardTimeout = 0;
          }
          document.removeEventListener('click', clearGuard, true);
          document.removeEventListener('keydown', clearGuard, true);
        };
        document.addEventListener('click', clearGuard, { capture: true, once: true });
        document.addEventListener('keydown', clearGuard, { capture: true, once: true });
      }
    }

    // Don't auto-load empty state on page load for inline search (causes unwanted dropdown opening)
    // Empty state will load on first user interaction (focus/search)
    // if (RecentlyViewed.getProducts().length > 0) {
    //   requestIdleCallback(() => {
    //     this.#loadEmptyState();
    //   });
    // }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    if (this.#pageLoadGuardTimeout) {
      window.clearTimeout(this.#pageLoadGuardTimeout);
      this.#pageLoadGuardTimeout = 0;
    }
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  /**
   * Handles the input focus event for inline search (non-modal).
   */
  #handleInputFocus = () => {
    if (window.__algoliaAutocompleteActive) {
      return;
    }

    if (this.#pageLoadGuardActive) {
      this.refs.searchInput?.blur();
      return;
    }
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput?.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else if (this.refs.searchInput) {
          const searchUrl = this.#buildLocaleAwareSearchUrl(Theme.routes.search_url);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    // Only reset if input is actually empty or we're explicitly clearing
    if (!this.refs.searchInput || this.refs.searchInput.value.trim() === '') {
      if (keepFocus) {
        this.refs.searchInput?.focus();
      }
      this.#resetSearch();
    }
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    if (window.__algoliaAutocompleteActive) return;

    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;
    if (!this.refs.searchInput) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#openInlineDropdown();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * Uses locale-aware URL so Arabic (and other non-default) storefronts get results in the correct language.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = this.#buildLocaleAwareSearchUrl(Theme.routes.predictive_search_url);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Build a URL for search/predictive search that includes the current locale prefix.
   * Ensures Arabic and other non-default locales hit the correct locale-specific search endpoint.
   * @param {string} routePath - Relative path from Theme.routes (e.g. /search/suggest or /ar/search/suggest)
   * @returns {URL}
   */
  #buildLocaleAwareSearchUrl(routePath) {
    const localeRoot = typeof Theme.locale_root === 'string' ? Theme.locale_root.replace(/\/$/, '') : '';
    const needsLocalePrefix = localeRoot && localeRoot !== '' && routePath && !routePath.startsWith(localeRoot);
    const path = needsLocalePrefix ? localeRoot + (routePath.startsWith('/') ? routePath : '/' + routePath) : routePath;
    return new URL(path, location.origin);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = this.#buildLocaleAwareSearchUrl(Theme.routes.search_url);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #openInlineDropdown() {
    if (this.dialog) return;

    this.classList.add('dropdown-open');

    const dropdown = this.querySelector('.predictive-search-form__content-wrapper--inline');
    if (dropdown instanceof HTMLElement) {
      dropdown.style.display = 'flex';
      dropdown.style.visibility = 'visible';
    }

    const results = this.querySelector('.predictive-search-dropdown');
    if (results instanceof HTMLElement) {
      results.setAttribute('aria-expanded', 'true');
    }

    this.refs.searchInput?.setAttribute('aria-expanded', 'true');
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    // Only clear input if it's not currently focused (user is not actively typing)
    if (searchInput && document.activeElement !== searchInput) {
      searchInput.value = '';
    }
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();
    const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

    if (viewedProducts.length > 0 && collectionElement) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        if (recentlyViewedProductsHtml) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }
          collectionElement.prepend(...recentlyViewedProductsHtml.children);
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#loadTrendingProducts();
  };

  /**
   * Algolia Recommend API config for trending items (search dropdown empty state).
   * Must match theme Algolia credentials.
   */
  static #algoliaConfig = {
    appId: '18TT9N4CSD',
    apiKey: '13f15ea1fb4b1badcdcb224b64a1fc69',
    indexName: 'shopify_products',
  };

  /**
   * Fetch Algolia trending items and render them into the search dropdown empty state.
   */
  async #loadTrendingProducts() {
    const list = this.querySelector('#predictive-search-trending-list');
    if (!list) return;

    const { appId, apiKey, indexName } = PredictiveSearchComponent.#algoliaConfig;
    if (!appId || !apiKey || !indexName) return;

    const isArabic =
      window.location.pathname.includes('/ar/') ||
      window.location.pathname.includes('/ar?') ||
      document.documentElement.lang === 'ar';
    const languageFilter = isArabic ? ' AND named_tags.locale:ar' : ' AND named_tags.locale:en';

    try {
      const res = await fetch('https://' + appId + '-dsn.algolia.net/1/indexes/*/recommendations', {
        method: 'POST',
        headers: {
          'X-Algolia-Application-Id': appId,
          'X-Algolia-API-Key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requests: [
            {
              indexName,
              model: 'trending-items',
              threshold: 0,
              maxRecommendations: 12,
              queryParameters: {
                distinct: true,
                filters: 'inventory_available:true' + languageFilter,
                attributesToRetrieve: ['objectID', 'title', 'handle', 'price', 'compare_at_price', 'image', 'id'],
              },
            },
          ],
        }),
      });

      if (!res.ok) return;
      const data = await res.json();
      const rawHits = data.results?.[0]?.hits || [];
      const seen = new Set();
      const hits = [];
      for (const h of rawHits) {
        if (seen.has(h.id)) continue;
        seen.add(h.id);
        hits.push(h);
        if (hits.length >= 8) break;
      }

      list.innerHTML = '';
      for (const hit of hits) {
        const url = '/products/' + (hit.handle || '');
        const title = (hit.title || '').replace(/"/g, '&quot;');
        const imgSrc = hit.image || '';
        const li = document.createElement('li');
        li.className = 'predictive-search-results__card predictive-search-results__card--product';
        li.setAttribute('ref', 'resultsItems[]');
        li.innerHTML =
          '<div class="resource-card" data-resource-type="product" href="' +
          url +
          '">' +
          '<a class="resource-card__link" href="' +
          url +
          '"><span class="visually-hidden">' +
          title +
          '</span></a>' +
          '<div class="resource-card__media" style="--resource-card-aspect-ratio: 1;">' +
          (imgSrc
            ? '<img class="resource-card__image" src="' + imgSrc + '" alt="' + title + '" width="500" height="500" loading="lazy">'
            : '<div class="resource-card__image-placeholder" aria-hidden="true">' + title + '</div>') +
          '</div>' +
          '</div>';
        list.appendChild(li);
      }
    } catch {
      // Silently ignore; dropdown still shows with empty trending list
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
